import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTaxYear } from '../context/TaxYearContext';
import { 
  Ship, 
  FileText, 
  LogOut,
  Menu,
  X,
  PoundSterling,
  Calendar,
  ChevronDown,
  ShieldCheck,
  BookOpen,
  Scale,
  Settings,
  UserPlus,
  Briefcase,
  Check,
  MessageSquare,
  ArrowRightLeft,
  Mail,
  CreditCard
} from 'lucide-react';
import { Button } from './ui/button';
import { PaywallBanner } from './PaywallBanner';
import { InstallAppPrompt } from './InstallAppPrompt';
import FeedbackDialog from './FeedbackDialog';
import SessionsDialog from './auth/SessionsDialog';
import VersionFooter from './VersionFooter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Compact year-picker dropdown used in both the mobile header and desktop top bar */
const YearSelector = ({ taxYears, selectedYear, setSelectedYear, className = '' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600/20 border border-amber-500/30 rounded-full hover:bg-amber-600/30 transition-colors"
        data-testid="year-selector-btn"
      >
        <Calendar className="h-3.5 w-3.5 text-amber-400" />
        <span className="text-xs font-medium text-amber-400">
          {selectedYear === 'all' ? 'All years' : selectedYear}
        </span>
        <ChevronDown className={`h-3 w-3 text-amber-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-40 bg-slate-800 border border-slate-600 rounded-lg shadow-xl z-[60] overflow-hidden">
          <button
            onClick={() => { setSelectedYear('all'); setOpen(false); }}
            className={`flex items-center justify-between w-full px-3 py-2 text-xs transition-colors ${selectedYear === 'all' ? 'text-amber-400 bg-amber-600/10' : 'text-slate-300 hover:bg-slate-700'}`}
            data-testid="year-option-all"
          >
            All years
            {selectedYear === 'all' && <Check className="h-3 w-3" />}
          </button>
          {taxYears.map(year => (
            <button
              key={year}
              onClick={() => { setSelectedYear(year); setOpen(false); }}
              className={`flex items-center justify-between w-full px-3 py-2 text-xs transition-colors ${selectedYear === year ? 'text-amber-400 bg-amber-600/10' : 'text-slate-300 hover:bg-slate-700'}`}
              data-testid={`year-option-${year}`}
            >
              {year}
              {selectedYear === year && <Check className="h-3 w-3" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { selectedYear, setSelectedYear } = useTaxYear();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [taxYears, setTaxYears] = useState([]);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [sessionsOpen, setSessionsOpen] = useState(false);
  const [feedbackUnread, setFeedbackUnread] = useState(0);
  const [messageUnread, setMessageUnread] = useState(0);
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  // Fetch unread feedback count
  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/feedback/unread-count`);
        setFeedbackUnread(res.data.unread_count || 0);
      } catch (err) {
        // ignore
      }
    };
    if (user) {
      fetchUnread();
      const iv = setInterval(fetchUnread, 30000);
      return () => clearInterval(iv);
    }
  }, [user]);

  // Fetch unread message count
  useEffect(() => {
    const fetchMsgUnread = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/messages/unread-count`);
        setMessageUnread(res.data.unread_count || 0);
      } catch {
        // ignore
      }
    };
    if (user) {
      fetchMsgUnread();
      const iv = setInterval(fetchMsgUnread, 15000);
      return () => clearInterval(iv);
    }
  }, [user]);

  // Fetch available tax years
  useEffect(() => {
    const fetchTaxYears = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/dashboard/tax-years`);
        setTaxYears(response.data.tax_years || []);
      } catch (error) {
      }
    };
    fetchTaxYears();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/guide', icon: BookOpen, label: 'How to Use' },
    { path: '/trips', icon: Ship, label: 'Trips' },
    { path: '/earnings', icon: PoundSterling, label: 'Pay & Contracts' },
    { path: '/eligibility', icon: ShieldCheck, label: 'SED Eligibility' },
    { path: '/reports', icon: FileText, label: 'End of Year Reports' },
    { path: '/messages', icon: Mail, label: 'Messages', badge: messageUnread },
    { path: '/my-accountant', icon: Briefcase, label: 'My Accountant' },
    { path: '/invite', icon: UserPlus, label: 'Invite a Friend' },
    ...(!isAdmin ? [{ path: '/subscription', icon: CreditCard, label: 'Subscription' }] : []),
    { path: '/settings', icon: Scale, label: 'Legal' },
    ...(isAdmin ? [{ path: '/admin', icon: Settings, label: 'Admin Panel', badge: feedbackUnread }] : []),
  ];

  const NavLink = ({ item }) => {
    const isActive = location.pathname === item.path || 
      (item.path === '/earnings' && (location.pathname === '/monthly-earnings' || location.pathname === '/documents' || location.pathname === '/expenses'));
    return (
      <Link
        to={item.path}
        onClick={() => setSidebarOpen(false)}
        className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 text-sm ${
          isActive 
            ? 'bg-amber-600 text-white shadow-lg shadow-amber-600/30' 
            : 'text-slate-400 hover:text-white hover:bg-slate-800'
        }`}
      >
        <item.icon className="h-4 w-4" />
        <span className="font-medium flex-1">{item.label}</span>
        {item.badge > 0 && (
          <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1" data-testid={`badge-${item.path.replace(/\//g, '-')}`}>
            {item.badge}
          </span>
        )}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-slate-800 border-b border-slate-700 z-40 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <img src="/logo.png" alt="Seafarer Logbook" className="h-7 w-7" />
          <span className="font-bold text-white">Seafarer Logbook</span>
        </div>
        <div className="flex items-center gap-2">
          {/* Mobile Year Selector */}
          <YearSelector taxYears={taxYears} selectedYear={selectedYear} setSelectedYear={setSelectedYear} />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-white"
          >
            {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-slate-800 border-r border-slate-700 z-50
        transform transition-transform duration-300 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-14 flex items-center justify-between px-5 border-b border-slate-700 shrink-0">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="Seafarer Logbook" className="h-8 w-8" />
              <h1 className="font-bold text-white text-sm">Seafarer Logbook</h1>
            </div>
          </div>

          {/* Switch to Accountant Portal — Admin+ only */}
          {(user?.role === 'superadmin') && (
            <div className="px-3 pt-2">
              <button onClick={() => navigate('/accountant/dashboard')}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-sky-500/10 border border-sky-500/20 hover:bg-sky-500/20 transition-colors"
                data-testid="toggle-accountant-portal">
                <ArrowRightLeft className="h-4 w-4 text-sky-400" />
                <span className="text-xs font-medium text-sky-400">Accountant Portal</span>
              </button>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 px-3 py-2 space-y-0.5 overflow-y-auto min-h-0">
            {navItems.map(item => (
              <NavLink key={item.path} item={item} />
            ))}
          </nav>

          {/* User Section */}
          <div className="px-3 py-2.5 border-t border-slate-700 shrink-0">
            <div className="flex items-center gap-2.5 mb-1.5 px-1">
              <div className="w-8 h-8 rounded-full bg-amber-600 flex items-center justify-center shrink-0">
                <span className="text-white font-semibold text-sm">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate leading-tight">{user?.name}</p>
                <p className="text-[11px] text-slate-400 truncate leading-tight">{user?.email}</p>
              </div>
            </div>
            {/* Subscription Status */}
            {user && (
              <div className="mb-1.5 px-1">
                {user.role === 'superadmin' ? (
                  <div className="flex items-center gap-1.5 text-[10px] text-rose-400">
                    <Settings className="h-3 w-3" />
                    <span>Admin+</span>
                  </div>
                ) : user.role === 'admin' ? (
                  <div className="flex items-center gap-1.5 text-[10px] text-purple-400">
                    <Settings className="h-3 w-3" />
                    <span>Administrator</span>
                  </div>
                ) : user.is_trial && user.trial_active ? (
                  <div className="flex items-center gap-1.5 text-[10px] text-emerald-400">
                    <span>Free trial — {user.trial_days_remaining} days remaining</span>
                  </div>
                ) : user.tier === 'premium' || user.tier === 'standard' ? (
                  <div className="flex items-center gap-1.5 text-[10px] text-amber-400">
                    <span>{user.tier === 'premium' ? 'Premium' : 'Standard'} plan</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
                    <span>Free plan</span>
                  </div>
                )}
              </div>
            )}
            <button
              onClick={() => {
                setFeedbackOpen(true);
                setSidebarOpen(false);
              }}
              className="w-full flex items-center gap-2 justify-start text-slate-400 hover:text-amber-400 hover:bg-amber-400/10 h-8 text-sm px-4 rounded-md transition-colors relative"
              data-testid="sidebar-feedback-btn"
            >
              <MessageSquare className="h-4 w-4" />
              Feedback
              {!isAdmin && feedbackUnread > 0 && (
                <span
                  className="ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
                  data-testid="feedback-reply-badge"
                >
                  {feedbackUnread}
                </span>
              )}
            </button>
            <button
              onClick={() => {
                setSessionsOpen(true);
                setSidebarOpen(false);
              }}
              className="w-full flex items-center gap-2 justify-start text-slate-400 hover:text-amber-400 hover:bg-amber-400/10 h-8 text-sm px-4 rounded-md transition-colors relative"
              data-testid="sidebar-sessions-btn"
            >
              <ShieldCheck className="h-4 w-4" />
              Active Sessions
            </button>
            <Button
              variant="ghost"
              onClick={handleLogout}
              data-testid="sign-out-btn"
              className="w-full justify-start text-slate-400 hover:text-red-400 hover:bg-red-400/10 h-8 text-sm"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
            {/* Made with Emergent */}
            <a
              href="https://app.emergent.sh/?utm_source=emergent-badge"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 mt-2 py-1.5 rounded-full bg-black/40 hover:bg-black/60 transition-colors"
              data-testid="emergent-badge-sidebar"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path d="M15.5702 8.13142C15.7729 8.0412 16.0007 8.18878 15.9892 8.4103C15.8374 11.3192 14.0965 14.0405 11.2531 15.3065C8.40964 16.5725 5.2224 16.0453 2.95912 14.2117C2.78676 14.072 2.82955 13.804 3.03219 13.7137L4.95677 12.8568C5.04866 12.8159 5.15446 12.823 5.24204 12.8725C6.73377 13.7153 8.59176 13.8649 10.2772 13.1145C11.9626 12.3641 13.0947 10.8833 13.4665 9.21075C13.4883 9.11256 13.5539 9.02918 13.6457 8.98827L15.5702 8.13142Z" fill="white"/>
                <path fillRule="evenodd" clipRule="evenodd" d="M15.3066 4.74698L15.5067 5.19653C15.5759 5.35178 15.5061 5.53366 15.3508 5.60278L1.29992 11.8586C1.14467 11.9278 0.962794 11.8579 0.893675 11.7027L0.701732 11.2716L0.693457 11.2531C-1.10317 7.21778 0.711626 2.49007 4.74692 0.693443C8.78221 -1.10318 13.51 0.711693 15.3066 4.74698ZM2.82356 8.55367C2.63552 8.63739 2.41991 8.51617 2.40853 8.31065C2.28373 6.05724 3.53858 3.85787 5.72286 2.88536C7.90715 1.91286 10.3813 2.45199 11.9724 4.05256C12.1175 4.19854 12.0633 4.43988 11.8753 4.5236L2.82356 8.55367Z" fill="white"/>
              </svg>
              <span className="text-[11px] font-semibold text-white/70">Made with Emergent</span>
            </a>
            <p className="text-[9px] text-slate-600 text-center mt-2 select-none" data-testid="copyright-notice">
              &copy; 2026 D Grassie. All rights reserved.
            </p>
            <VersionFooter />
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen overflow-x-hidden">
        {/* Desktop Year Selector — top-right */}
        <div className="hidden lg:flex justify-end px-6 pt-4 pb-0">
          <YearSelector taxYears={taxYears} selectedYear={selectedYear} setSelectedYear={setSelectedYear} />
        </div>
        <div className="p-4 sm:p-6 lg:pt-2 space-y-4">
          <PaywallBanner />
          <InstallAppPrompt />
          {children}
        </div>
      </main>
      <FeedbackDialog
        open={feedbackOpen}
        onOpenChange={setFeedbackOpen}
        unreadReplies={!isAdmin ? feedbackUnread : 0}
        onRepliesRead={() => setFeedbackUnread(0)}
      />
      <SessionsDialog open={sessionsOpen} onOpenChange={setSessionsOpen} />
    </div>
  );
};

export default Layout;
